import { Controller } from "@hotwired/stimulus"

// Layers a compact, dismissible "resolved" token on top of the generic place-autocomplete
// controller, driven entirely by its "place:picked" / "place:invalidated" events. Text/colors
// mirror the SD-2466 territory-check UI (territory_check_autocomplete.js / territory_check.css).
//
// The scope line + ZIP<->city toggle is opt-in (scopeToggleValue) - the territory check field can
// resolve to either a ZIP or a city, so it needs both; a plain city field (Franchise Fit Survey's
// location preferences) never does, and gets just the token.
export default class extends Controller {
  static targets = [
    "input", "label", "token", "tokenIcon", "tokenPrimary", "tokenSecondary",
    "scopeChange", "scopeNote", "edit", "remove", "actions"
  ]

  static values = { scopeToggle: { type: Boolean, default: false } }

  connect() {
    this.current = null
    this.mode = null
    this.onPicked = this.onPicked.bind(this)
    this.onInvalidated = this.onInvalidated.bind(this)
    this.inputTarget.addEventListener("place:picked", this.onPicked)
    this.inputTarget.addEventListener("place:invalidated", this.onInvalidated)
    // A cloned column (Add New Location) or a Turbo restore carries whatever token markup the
    // source column had, with no JS state behind it - always start from "nothing picked," then
    // hydrate to a token if the value on the page is itself already a trusted, resolved pick.
    this.hydrate()
  }

  disconnect() {
    this.inputTarget.removeEventListener("place:picked", this.onPicked)
    this.inputTarget.removeEventListener("place:invalidated", this.onInvalidated)
  }

  // A value rendered from the database (a past survey, HubSpot, an earlier territory check) is
  // seeded `data-place-verified="true"` by the server (see Google::PlacesHelper) precisely so it
  // reads as resolved rather than raw, unconfirmed typing - render it as a token immediately
  // instead of leaving it looking like plain text waiting to be picked again.
  hydrate() {
    const value = this.inputTarget.value.trim()
    if (this.inputTarget.dataset.placeVerified !== "true" || !value) {
      this.render()
      return
    }

    const isZip = this.scopeToggleValue && /^\d+$/.test(value)
    const state = this.hydratedState()

    this.current = isZip
      ? { kind: "postal_code", postal_code: value, state }
      : { kind: "city", city: value, state }
    this.mode = isZip ? "zip" : "city"
    this.render()
  }

  hydratedState() {
    const base = this.baseController()
    const selector = base?.fillValue?.state
    if (!selector) return null

    return base.scopeElement()?.querySelector(selector)?.value || null
  }

  onPicked(event) {
    this.current = event.detail.place
    this.mode = this.scopeToggleValue && this.current.kind === "postal_code" ? "zip" : "city"
    this.render()
  }

  onInvalidated() {
    this.current = null
    this.mode = null
    this.render()
  }

  // The pencil. Hands the input back holding the resolved value, selected - so typing replaces
  // it outright, while a small correction stays possible. Escape puts the token back.
  edit() {
    this.baseController()?.invalidate()
    this.inputTarget.focus()
    this.inputTarget.select()
  }

  // The trash, where a field can legitimately end up empty. Elsewhere the trash removes the whole
  // cell instead, and the view wires it to that; either way it means "get rid of this entry".
  discard() {
    this.baseController()?.discard()
    this.inputTarget.focus()
  }

  // Flips between the ZIP and the city it belongs to, using the pair already returned by the one
  // resolve call - never a second lookup.
  toggleScope() {
    if (!this.current) return

    const nextMode = this.mode === "zip" ? "city" : "zip"
    const value = nextMode === "zip" ? this.current.postal_code : this.current.city
    if (!value) return

    this.mode = nextMode
    const base = this.baseController()
    if (base) base.setVerifiedValue(value)
    this.render()
  }

  baseController() {
    // Both controllers share the same data-controller root (this.element), not the input itself.
    return this.application.getControllerForElementAndIdentifier(this.element, "place-autocomplete")
  }

  render() {
    const verified = Boolean(this.current)
    this.inputTarget.classList.toggle("d-none", verified)
    this.tokenTarget.classList.toggle("d-none", !verified)
    // A Bootstrap floating label sits absolutely positioned over the input; with the input
    // hidden it would float over the token instead, so it hides along with the input.
    if (this.hasLabelTarget) this.labelTarget.classList.toggle("d-none", verified)

    if (this.hasEditTarget) this.editTarget.classList.toggle("d-none", !verified)
    // Only where the caller asked the trash to follow the token; a removable cell keeps its trash
    // whether or not anything is picked in it.
    if (this.hasRemoveTarget) this.removeTarget.classList.toggle("d-none", !verified)
    this.collapseActions()

    if (!verified) {
      if (this.hasScopeChangeTarget) this.scopeChangeTarget.classList.add("d-none")
      if (this.hasScopeNoteTarget) this.scopeNoteTarget.classList.add("d-none")
      return
    }

    this.renderToken()
    if (this.scopeToggleValue) this.renderScopeChange(this.mode === "zip")
  }

  // Beside the field the row occupies width even when empty, so it goes away with its buttons.
  collapseActions() {
    if (!this.hasActionsTarget) return

    const shown = Array.from(this.actionsTarget.children).some((b) => !b.classList.contains("d-none"))
    this.actionsTarget.classList.toggle("d-none", !shown)
  }

  renderToken() {
    const zip = this.mode === "zip"
    const state = this.current.state || this.current.state_code
    const cityLine = [this.current.city, state].filter(Boolean).join(", ")
    const zipLine = this.current.postal_code ? `ZIP ${this.current.postal_code}` : ""

    this.tokenPrimaryTarget.textContent = zip ? zipLine : cityLine

    // Mirrored: whichever scope is not the primary line sits under it, so a ZIP flipped to its
    // city still shows the ZIP it came from. A city picked directly carries no ZIP at all
    // (Google returns none for a city - it spans many), so that token stands alone.
    const secondary = zip ? cityLine : zipLine
    this.tokenSecondaryTarget.textContent = secondary
    this.tokenSecondaryTarget.classList.toggle("d-none", !secondary)

    // Tone is always "success" here - a token only ever renders from a resolved pick.
    if (this.hasTokenIconTarget) this.tokenIconTarget.className = "selection-token-icon fa-solid fa-circle-check"
    this.tokenTarget.classList.remove("tone-danger", "tone-warning")
    this.tokenTarget.classList.add("tone-success")
  }

  // The toggle is the only scope UI - no separate sentence explaining the scope, just the link
  // that changes it. Only ever shown when there is a ZIP in play for this pick: a city typed and
  // picked directly never told us any ZIP, so there is nothing to switch to.
  renderScopeChange(isZip) {
    const hasCity = Boolean(this.current.city)
    const hasZip = Boolean(this.current.postal_code)
    const canToggle = hasCity && hasZip

    if (this.hasScopeNoteTarget) {
      this.scopeNoteTarget.classList.toggle("d-none", canToggle)
      this.scopeNoteTarget.textContent = isZip
        ? "No city option is available for this ZIP code."
        : "This selection covers the city, not a specific ZIP code."
    }

    if (isZip && hasCity) {
      const state = this.current.state || this.current.state_code
      this.scopeChangeTarget.textContent = `Change ZIP check to ${[this.current.city, state].filter(Boolean).join(", ")} format`
      this.scopeChangeTarget.classList.remove("d-none")
    } else if (!isZip && hasZip) {
      const place = [this.current.city, this.current.state_code].filter(Boolean).join(", ")
      this.scopeChangeTarget.textContent = `Change ${place} check to ${this.current.postal_code}`
      this.scopeChangeTarget.classList.remove("d-none")
    } else {
      this.scopeChangeTarget.classList.add("d-none")
    }
  }
}
